import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SubscriptionInterval, ServiceTheme, ThemeLayout, AssetValidationStatus } from '@/types/service';
import { formatPrice } from '@/types/service';
import { useAssetValidator } from '@/hooks/use-asset-validator';
import { useStylesheetLoader } from '@/hooks/use-stylesheet-loader';
import { sanitizeCss, prefixCssSelectors } from '@/utils/css-prefix';
import type { DeviceMode } from './preview-device-toggle';
import './payment-page-preview.css';

/** Same sandbox scope the stylesheet loader uses (keeps URL- and rawCss-paths consistent). */
const PREVIEW_CSS_PREFIX = '.preview-container';

interface PaymentPagePreviewProps {
  /** Service description shown to users */
  description: string;
  /** Amount in cents */
  grossAmount: number;
  /** Subscription interval */
  subscriptionInterval: SubscriptionInterval;
  /** Theme configuration */
  theme?: ServiceTheme;
  /** Device preview mode */
  deviceMode?: DeviceMode;
  /**
   * Service theme-layout — controls layout-specific rendering.
   * - `gameloft` / `two-zone-v2`: Two-Zone-Layout — background-image full-opacity as
   *   top-zone, no separate header-placeholder when only backgroundImage is set
   *   (Spec 0218; two-zone-v2 added with Contract v2.15 / #336). The preview is a
   *   best-effort approximation — the Facade renders the real page.
   * - `default`/`minimal`/`undefined`: atmospheric background-image (opacity 0.15),
   *   header-placeholder always shown (legacy behavior).
   */
  themeLayout?: ThemeLayout;
  /** Additional CSS classes */
  className?: string;
  /** Callback when asset validation status changes */
  onAssetValidation?: (status: AssetValidationStatus) => void;
  /**
   * Inline CSS to preview directly (Spec 340 theme editor): when set, it is
   * sandboxed (`sanitizeCss` + `prefixCssSelectors`) and injected via the same
   * `<style>` path, taking precedence over `theme.stylesheetUrl`. Used for live
   * preview of generated-but-unsaved two-zone-v2 CSS (a `blob:` URL would be
   * rejected by `useStylesheetLoader`'s https-only guard).
   */
  rawCss?: string;
}

/**
 * Live preview of the payment page as it will appear to end users.
 * Phase 3: With stylesheet loading and CSS sandboxing.
 */
export function PaymentPagePreview({
  description,
  grossAmount,
  subscriptionInterval,
  theme,
  deviceMode = 'mobile',
  themeLayout,
  className,
  onAssetValidation,
  rawCss,
}: PaymentPagePreviewProps) {
  const { t } = useTranslation('services');
  const buttonText = t(`buttonText.${String(theme?.buttonTextId ?? 1)}`);
  const priceText = grossAmount > 0
    ? formatPrice(grossAmount) + ' / ' + t(`intervalShort.${subscriptionInterval}`)
    : '0,00 \u20AC / ---';

  // Asset validation hooks
  const headerImage = useAssetValidator(theme?.headerImageUrl, 'image');
  const backgroundImage = useAssetValidator(theme?.backgroundImageUrl, 'image');
  const stylesheet = useStylesheetLoader(theme?.stylesheetUrl);

  // Report asset validation status to parent
  useEffect(() => {
    if (onAssetValidation) {
      onAssetValidation({
        headerImage: headerImage.status,
        backgroundImage: backgroundImage.status,
        stylesheet: stylesheet.status,
      });
    }
  }, [headerImage.status, backgroundImage.status, stylesheet.status, onAssetValidation]);

  // Spec 0218 / Contract v2.15 (#336): Two-Zone-Rendering fuer gameloft UND two-zone-v2
  // (background-image full-opacity als Top-Zone, kein "Kein Header-Bild"-Placeholder
  // wenn nur backgroundImageUrl gesetzt ist). two-zone-v2 wird best-effort wie gameloft
  // dargestellt — die echte Seite rendert die Facade.
  // Spec 340: live-preview of generated-but-unsaved CSS. Sandboxed the same way the
  // loader sandboxes a fetched stylesheet, and given precedence over the URL path.
  const injectedCss = useMemo(
    () => (rawCss ? prefixCssSelectors(sanitizeCss(rawCss), PREVIEW_CSS_PREFIX) : null),
    [rawCss],
  );
  const effectiveCss = injectedCss ?? stylesheet.prefixedCss;

  const isTwoZoneLayout = themeLayout === 'gameloft' || themeLayout === 'two-zone-v2';
  const hasOnlyBackgroundImage = !theme?.headerImageUrl && !!theme?.backgroundImageUrl;
  const skipHeaderPlaceholder = isTwoZoneLayout && hasOnlyBackgroundImage;

  return (
    <div
      className={cn(
        'preview-container',
        deviceMode === 'desktop' && 'preview-container--desktop',
        isTwoZoneLayout && 'preview-container--gameloft',
        className
      )}
      role="img"
      aria-label="Payment Page Vorschau"
    >
      {/* Injected stylesheet — safe: sandboxed via prefix scope (loader or rawCss path) */}
      {effectiveCss && (
        <style dangerouslySetInnerHTML={{ __html: effectiveCss }} />
      )}

      {/* Background Image (if loaded) */}
      {backgroundImage.loadedUrl && (
        <img
          src={backgroundImage.loadedUrl}
          alt=""
          className="preview-background-image"
          aria-hidden="true"
        />
      )}

      {/* Header Area — Spec 0218: skip "Kein Header-Bild"-Placeholder bei gameloft + nur backgroundImage */}
      {!skipHeaderPlaceholder && (
        <div className="preview-header">
          {headerImage.loadedUrl ? (
            <img
              src={headerImage.loadedUrl}
              alt={t('preview.headerImage', { defaultValue: 'Service Header' })}
              className="preview-header__image"
            />
          ) : headerImage.status.status === 'loading' ? (
            <div className="preview-header__placeholder preview-header__placeholder--loading">
              <span className="text-xs text-muted-foreground">Lädt...</span>
            </div>
          ) : headerImage.status.status === 'error' ? (
            <div className="preview-header__placeholder preview-header__placeholder--error">
              <span className="text-xs text-destructive">
                {headerImage.status.message}
              </span>
            </div>
          ) : theme?.headerImageUrl ? (
            <div className="preview-header__placeholder preview-header__placeholder--with-url">
              <span className="text-xs text-muted-foreground">Header-Bild</span>
              <span className="text-xs text-muted-foreground truncate max-w-full">
                {theme.headerImageUrl.length > 30
                  ? `${theme.headerImageUrl.slice(0, 30)}...`
                  : theme.headerImageUrl}
              </span>
            </div>
          ) : (
            <div className="preview-header__placeholder">
              <span className="text-xs text-muted-foreground">Kein Header-Bild</span>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="preview-content">
        {/* Description */}
        <h2 className="preview-description">
          {description || 'Service-Beschreibung'}
        </h2>

        {/* Price */}
        <p className="preview-price">{priceText}</p>

        {/* MSISDN Input (Mock) */}
        <div className="preview-msisdn">
          <input
            type="text"
            className="preview-msisdn__input"
            placeholder="49"
            disabled
            aria-label="Telefonnummer (Vorschau)"
          />
        </div>

        {/* Submit Button — also carries the real Facade CTA class so a loaded /
            generated two-zone-v2 stylesheet (which targets `.tz-cta-btn`) actually
            styles the preview button. */}
        <button
          type="button"
          className="preview-button tz-cta-btn"
          disabled
          aria-label="Kaufen Button (Vorschau)"
        >
          {buttonText}
        </button>
      </div>

      {/* Footer */}
      <div className="preview-footer">
        <span>Datenschutz</span>
        <span className="preview-footer__separator">|</span>
        <span>AGB</span>
        <span className="preview-footer__separator">|</span>
        <span>Impressum</span>
      </div>

      {/* Background Status Indicator (when URL set but not loaded) */}
      {theme?.backgroundImageUrl && !backgroundImage.loadedUrl && (
        <div className={cn(
          'preview-background-hint',
          backgroundImage.status.status === 'error' && 'preview-background-hint--error'
        )}>
          <span className="text-xs">
            {backgroundImage.status.status === 'loading' && 'Hintergrund wird geladen...'}
            {backgroundImage.status.status === 'error' && `Hintergrund: ${backgroundImage.status.message}`}
            {backgroundImage.status.status === 'empty' && `Hintergrund: ${theme.backgroundImageUrl.slice(0, 25)}...`}
          </span>
        </div>
      )}

      {/* Stylesheet Status Indicator */}
      {theme?.stylesheetUrl && stylesheet.status.status !== 'success' && (
        <div className={cn(
          'preview-stylesheet-hint',
          stylesheet.status.status === 'error' && 'preview-stylesheet-hint--error'
        )}>
          <span className="text-xs">
            {stylesheet.status.status === 'loading' && 'Stylesheet wird geladen...'}
            {stylesheet.status.status === 'error' && `Stylesheet: ${stylesheet.status.message}`}
          </span>
        </div>
      )}
    </div>
  );
}
